import React from 'react';
import type { Theme } from '@material-ui/core/styles/createMuiTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import { makeStyles } from '@material-ui/styles';
import { Page } from 'react-pdf';
import { MD_MEDIA_QUERY } from '../../common/constants';
import type { IEnvelopeState } from '../../store/reducers/envelope';
import type { IBlock } from '../../store/types';
import { NextButton } from '../NextButton';

const useStyles = makeStyles<Theme, PdfPageProps>(
  () => ({
    responsiveDocument: {
      '@global canvas': {
        width: '100% !important',
        height: 'auto !important',
      },
      position: 'relative',
    },
    '@global': {
      '.linkAnnotation': (p) => ({
        width: `${p.isMobile ? '50px !important' : ''}`,
        height: `${p.isMobile ? '5px !important' : ''}`,
        top: `${p.isMobile ? '0px !important' : ''}`,
        left: `${p.isMobile ? '0px !important' : ''}`,
      }),
    },
  }),
  { name: 'PdfPage' }
);

export interface IAnnotation {
  id: string;
  width: number;
  height: number;
  x: number;
  y: number;
  url: string;
}

export type PdfPageProps = {
  blocks: { [blockId: string]: IBlock | undefined };
  signingExperienceBlocks: IBlock[];
  documentId: string;
  documentIndex: number;
  pageNumber: number;
  zoom: number;
  /** 1-based index of this page relative to all other pages in all other documents */
  crossDocPageIndex: number;
  envelope: IEnvelopeState;
  currentSelectedBlockIndex: number | null;
  allBlocksComplete: boolean;
  scrollToNextBlock: () => void;
  onLoadSuccess?: (page: any, crossDocPageIndex: number) => void;
  pageDimensions: { width?: number; height?: number };
  pageScale: number;
  setPageScale: (crossDocPageIndex: number, pageScale: number) => void;
  isMobile: boolean;
  selectedBlock: IBlock | undefined;
};

export const PdfPage = React.memo(function PdfPage(props: PdfPageProps) {
  // The reason we use isMobile is on phones in portrait mode, the media query won't trigger. This
  // also prevents being fully zoomed out while on small desktop windows.
  const {
    signingExperienceBlocks,
    documentId,
    currentSelectedBlockIndex,
    pageNumber,
    zoom,
    pageScale,
    setPageScale,
    onLoadSuccess,
    pageDimensions,
    crossDocPageIndex,
    scrollToNextBlock,
    allBlocksComplete,
    envelope,
    documentIndex,
    selectedBlock,
  } = props;

  const isMobileWidth = useMediaQuery(MD_MEDIA_QUERY);
  const classes = useStyles(props);

  const pageRef = useSetPageScale({
    pageWidth: pageDimensions?.width || 0,
    currentPageScale: pageScale,
    setPageScale: (scale) => setPageScale(crossDocPageIndex, scale),
  });

  //  This was clearly intended to be used at some point but setPageAnnotations was no longer being called so it would always be undefine???
  // const [pageAnnotations, setPageAnnotations] = React.useState<IAnnotation[] | undefined>();

  function handlePageLoadSuccess(page: any) {
    onLoadSuccess?.(page, crossDocPageIndex);
  }

  const isPageFocused =
    currentSelectedBlockIndex !== null && signingExperienceBlocks[currentSelectedBlockIndex].pageNumber === pageNumber;
  const pageClass = isMobileWidth ? classes.responsiveDocument : undefined;

  return (
    <>
      {isPageFocused || (currentSelectedBlockIndex === null && pageNumber === 0 && !allBlocksComplete)
        ? (selectedBlock || pageNumber === 0) &&
          !isMobileWidth &&
          envelope.envelope!.signer.signingStatus !== 'Complete' && (
            <NextButton
              onClick={scrollToNextBlock}
              selectedBlock={selectedBlock}
              zoom={zoom}
              currentSelectedBlockIndex={currentSelectedBlockIndex}
              documentIndex={documentIndex}
              documentId={documentId}
              pageDimensions={pageDimensions}
            />
          )
        : null}

      <Page
        inputRef={(ref) => (pageRef.current = ref)}
        pageNumber={pageNumber + 1}
        scale={zoom}
        className={pageClass}
        loading=""
        devicePixelRatio={isMobileWidth ? 0.75 : (window.devicePixelRatio ?? 1)}
        renderTextLayer={false}
        onLoadSuccess={crossDocPageIndex === 1 || !pageDimensions.height ? handlePageLoadSuccess : undefined}
      />

      {/* This was clearly intended to be used at some point but setPageAnnotations was no longer being called so it would always be undefine??? */}
      {/* <PageAnnotations pageAnnotations={pageAnnotations} isMobile={isMobileWidth} /> */}
    </>
  );
});

function useSetPageScale(args: {
  pageWidth: number;
  currentPageScale: number;
  setPageScale: (pageScale: number) => void;
}) {
  const { pageWidth, currentPageScale, setPageScale } = args;
  const pageRef = React.useRef<HTMLDivElement | null>(null);
  const pagePixelWidth = pageRef?.current?.clientWidth;

  function recalculatePageScale() {
    if (!pageRef.current || !pageWidth || pagePixelWidth == null) return;
    let scale = pagePixelWidth / pageWidth;
    if (scale !== currentPageScale) setPageScale(scale);
  }

  React.useEffect(() => {
    if (!currentPageScale) recalculatePageScale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePixelWidth]);

  return pageRef;
}
